/* eslint-disable no-param-reassign */

import {
  RESET_STATE,
  CREATE_CONTENT,
  UPDATE_CONTENT_COLLECTION,
  DELETE_CONTENT,
  SELECT_NODE_ID,
  SELECT_PARENT_FOLDER,
  END_EDITING_CONTENT_NODE,
  SET_NOT_EDITING,
  SET_TITLE,
  SET_ICON,
  SET_TEXT,
  SET_UPLOADING_IMAGE,
  REMOVE_UPLOADING,
  ADD_FOLDER,
  ADD_AND_SELECT_FOLDER,
  ADD_TEXT_NODE,
  ADD_AND_SELECT_TEXT_NODE,
  ADD_UPLOADING_IMAGE_NODE,
  MOVE_NODE,
  REMOVE_NODE,
  TOGGLE_EXPANDED,
  UNDO_CONTENT_CHANGE,
  REDO_CONTENT_CHANGE,
  UPDATE_PERMISSIONS,
  ADD_RECEIVED_DOCUMENT,
  REMOVE_RECEIVED_DOCUMENT,
  SET_BOARDS_ORDER,
  SET_USER_CONTENT_SETTINGS,
} from '../actions'

import * as Content from './Content'
import * as ContentCollection from './ContentCollection'
import * as ContentUICollection from './ContentUICollection'
import * as UndoStack from './UndoStack'

import { selectedContent } from '../selectors'

function stateSelectNodeId(state, nodeId, contentId, toggleNode = false) {
  const { selectedContentId, selectedNodeId } = state.app
  let contentUICollection = state.contentUICollection
  if (
    toggleNode &&
    selectedContentId === contentId &&
    selectedNodeId === nodeId
  ) {
    const result = stateToggleExpanded(state, nodeId, contentId)
    contentUICollection = result.contentUICollection
  }

  return {
    ...state,
    app: {
      ...state.app,
      selectedNodeId: nodeId,
      selectedContentId: contentId,
    },
    contentUICollection,
  }
}

function stateUpdateContentCollection(state, modified, removed) {
  const ownContentCollection = ContentCollection.withUpdates(
    state.ownContentCollection,
    modified,
    removed,
  )

  let { selectedContentId } = state.app
  let { selectedNodeId } = state.app

  return {
    ...state,
    ownContentCollection,
    app: {
      ...state.app,
      loading: false,
      selectedContentId,
      selectedNodeId,
    },
  }
}

function stateCreateContent(state, contentId, userId) {
  const content = {
    contentId,
    id: 0,
    icon: ContentCollection.randomUnusedEmoji(state.ownContentCollection),
    content: [],
  }

  const contentIndex = `${userId}-${contentId}`
  state = stateUpdateContentCollection(state, { [contentIndex]: content }, [])

  return stateSelectNodeId(state, 0, contentIndex)
}

function stateDeleteContent(state, contentId) {
  return stateUpdateContentCollection(state, [], [contentId])
}

function undoStackWithContentAdded(state) {
  const contentId = state.app.selectedContentId
  const content = state.ownContentCollection[contentId]
  return UndoStack.withContentAdded(state.app.undoStack, contentId, content)
}

function stateSelectParentFolder(state) {
  const {
    ownContentCollection,
    receivedContentCollection,
    app: { selectedNodeId, selectedContentId },
  } = state
  const boards = { ...ownContentCollection, ...receivedContentCollection }
  const content = boards[selectedContentId]

  const parent = Content.parentOfId(content, selectedNodeId)

  return parent
    ? stateSelectNodeId(state, parent.id, state.app.selectedContentId)
    : state
}

function stateSetNotEditing(state) {
  const wasEditing = !!state.app.editing

  if (!wasEditing) return state

  return {
    ...state,
    app: {
      ...state.app,
      editing: false,
    },
  }
}

function stateWithTopUndoEntryRemoved(state) {
  const undoStack = UndoStack.withTopUndoEntryRemoved(state.app.undoStack)
  if (undoStack === state.app.undoStack) {
    return state
  }
  return {
    ...state,
    app: {
      ...state.app,
      undoStack,
    },
  }
}

function stateEndEditingContentNode(state) {
  state = stateSetNotEditing(state)

  const { selectedContentId } = state.app
  const { selectedNodeId } = state.app

  if (!selectedContentId || !selectedNodeId) return state

  const boards = {
    ...state.ownContentCollection,
    ...state.receivedContentCollection,
  }
  const content = boards[selectedContentId]

  if (!content) return state

  const parent = Content.parentOfId(content, selectedNodeId)

  const newCollection = ContentCollection.withNodeRemovedIfEmpty(
    boards,
    selectedContentId,
    selectedNodeId,
  )

  if (newCollection !== state.ownContentCollection) {
    state = {
      ...state,
      ownContentCollection: newCollection,
    }
  }

  if (state.app.undoStack.contentId === selectedContentId) {
    // TODO: THIS HORRIBLE HACK IS USED TO CLEAN UP MEANINGLESS UNDO STACK ENTRIES
    // WHEN NEW CONTENT NODE IS ADDED - FIND A BETTER WAY
    let { undoStack } = state.app
    if (undoStack.undo.length > 0) {
      // remove top undoStack entry if it contains empty node
      const top = undoStack.undo.slice(-1)[0]
      if (Content.isEmptyContentNode(top, selectedNodeId)) {
        state = stateWithTopUndoEntryRemoved(state)
      }
    }
    undoStack = state.app.undoStack
    if (undoStack.undo.length > 0) {
      // remove top undoStack entry if it contains current state
      const top = undoStack.undo.slice(-1)[0]
      if (
        JSON.stringify({ ...top, nextId: 0 }) ===
        JSON.stringify({ ...content, nextId: 0 })
      ) {
        state = stateWithTopUndoEntryRemoved(state)
      }
    }
  }

  if (parent) state = stateSelectNodeId(state, parent.id, selectedContentId)

  return state
}

function stateSetIcon(state, nodeId, icon) {
  const newCollection = ContentCollection.withIconSet(
    state.ownContentCollection,
    state.app.selectedContentId,
    nodeId,
    icon,
  )

  if (newCollection === state.ownContentCollection) return state

  return {
    ...state,
    ownContentCollection: newCollection,
    app: {
      ...state.app,
      undoStack: undoStackWithContentAdded(state),
    },
  }
}

function stateSetTitle(state, nodeId, title, isEditing) {
  const wasEditing = !!state.app.editing

  const newCollection = ContentCollection.withTitleSet(
    state.ownContentCollection,
    state.app.selectedContentId,
    nodeId,
    title,
  )

  if (isEditing === wasEditing && newCollection === state.ownContentCollection)
    return state

  if (wasEditing)
    // editing in progress, no change to undo stack
    return {
      ...state,
      ownContentCollection: newCollection,
    }

  return {
    ...state,
    ownContentCollection: newCollection,
    app: {
      ...state.app,
      editing: isEditing,
      undoStack: undoStackWithContentAdded(state),
    },
  }
}

function stateSetText(state, nodeId, text, editing) {
  const wasEditing = !!state.app.editing

  const newCollection = ContentCollection.withTextSet(
    state.ownContentCollection,
    state.app.selectedContentId,
    nodeId,
    text,
  )

  if (editing === wasEditing && newCollection === state.ownContentCollection)
    return state

  if (wasEditing)
    return {
      ...state,
      ownContentCollection: newCollection,
    }

  return {
    ...state,
    ownContentCollection: newCollection,
    app: {
      ...state.app,
      editing,
      undoStack: undoStackWithContentAdded(state),
    },
  }
}

function stateRemoveUploading(state, image) {
  return {
    ...state,
    app: {
      ...state.app,
      uploading: {
        ...state.app.uploading,
        [image]: undefined,
      },
    },
  }
}

function stateSetUploadingImage(state, nodeId, image) {
  return {
    ...state,
    ownContentCollection: ContentCollection.withImageSet(
      state.ownContentCollection,
      state.app.selectedContentId,
      nodeId,
      image,
    ),
    app: {
      ...state.app,
      undoStack: undoStackWithContentAdded(state),
      uploading: {
        ...state.app.uploading,
        [image]: true,
      },
    },
  }
}

function stateAddFolder(state, parentId) {
  return {
    ...state,
    ownContentCollection: ContentCollection.withFolderAdded(
      state.ownContentCollection,
      state.app.selectedContentId,
      parentId,
    ),
    app: {
      ...state.app,
      undoStack: undoStackWithContentAdded(state),
    },
  }
}

function stateAddAndSelectFolder(state, parentId) {
  const contentId = state.app.selectedContentId
  if (contentId === undefined) {
    return state
  }

  state = stateAddFolder(state, parentId)

  const content = state.ownContentCollection[contentId]
  if (content === undefined) {
    return state
  }

  return stateSelectNodeId(state, content.nextId - 1, contentId)
}

function stateAddTextNode(state, parentId) {
  return {
    ...state,
    ownContentCollection: ContentCollection.withTextNodeAdded(
      state.ownContentCollection,
      state.app.selectedContentId,
      parentId,
    ),
    app: {
      ...state.app,
      undoStack: undoStackWithContentAdded(state),
    },
  }
}

function stateAddAndSelectTextNode(state, parentId) {
  const contentId = state.app.selectedContentId
  if (contentId === undefined) {
    return state
  }

  state = stateAddTextNode(state, parentId)

  const content = state.ownContentCollection[contentId]
  if (content === undefined) {
    return state
  }

  return stateSelectNodeId(state, content.nextId - 1, contentId)
}

function stateAddUploadingImageNode(state, parentId, image) {
  return {
    ...state,
    ownContentCollection: ContentCollection.withImageNodeAdded(
      state.ownContentCollection,
      state.app.selectedContentId,
      parentId,
      image,
    ),
    app: {
      ...state.app,
      undoStack: undoStackWithContentAdded(state),
      uploading: {
        ...state.app.uploading,
        [image]: true,
      },
    },
  }
}

function stateMoveNode(state, nodeId, newIndex) {
  const content = selectedContent(state)
  if (content === undefined) {
    return state
  }
  const parent = Content.parentOfId(content, nodeId)
  if (parent === null) {
    return state
  }

  const ownContentCollection = ContentCollection.withNodeMoved(
    state.ownContentCollection,
    state.app.selectedContentId,
    parent.id,
    nodeId,
    newIndex,
  )
  if (ownContentCollection === state.ownContentCollection) {
    return state
  }
  return {
    ...state,
    ownContentCollection,
    app: {
      ...state.app,
      undoStack: undoStackWithContentAdded(state),
    },
  }
}

function stateRemoveNode(state, nodeId) {
  const content = selectedContent(state)
  if (content === undefined) {
    return state
  }
  const parent = Content.parentOfId(content, nodeId)
  if (parent === null) {
    return state
  }
  // FIXME: check if previousSelectedNodeId is descendant of nodeId
  const previousSelectedNodeId = state.app.selectedNodeId
  const selectedNodeId =
    previousSelectedNodeId === nodeId ? parent.id : previousSelectedNodeId
  return {
    ...state,
    ownContentCollection: ContentCollection.withNodeRemoved(
      state.ownContentCollection,
      state.app.selectedContentId,
      parent.id,
      nodeId,
    ),
    app: {
      ...state.app,
      selectedNodeId,
      undoStack: undoStackWithContentAdded(state),
    },
  }
}

function stateToggleExpanded(state, folderId, contentId) {
  return {
    ...state,
    contentUICollection: ContentUICollection.byTogglingExpanded(
      state.contentUICollection,
      contentId,
      folderId,
    ),
  }
}

function stateUndoRedoContentChange(state, undo) {
  const contentId = state.app.selectedContentId
  if (contentId === undefined) return state

  const content = state.ownContentCollection[contentId]
  if (content === undefined) return state

  const selectedPath = state.app.selectedNodeId
    ? Content.pathOfId(content, state.app.selectedNodeId)
    : []

  const result = undo
    ? UndoStack.undo(state.app.undoStack, contentId, content)
    : UndoStack.redo(state.app.undoStack, contentId, content)

  if (result.content === undefined) return state

  const selectedNodeId = Content.bestIdWithPath(result.content, selectedPath)

  return {
    ...state,
    ownContentCollection: ContentCollection.withContentSet(
      state.ownContentCollection,
      contentId,
      result.content,
    ),
    app: {
      ...state.app,
      undoStack: result.undoStack,
      selectedNodeId,
    },
  }
}

function stateUpdatePermissions(state, permissions) {
  return {
    ...state,
    app: {
      ...state.app,
      permissions,
    },
  }
}

function addReceivedDocument(state, document, documentOwnerId) {
  return {
    ...state,
    receivedContentCollection: {
      ...state.receivedContentCollection,
      [`${documentOwnerId}-${document.contentId}`]: document,
    },
  }
}

function removeReceivedDocument(state, documentId) {
  const receivedContentCollection = { ...state.receivedContentCollection }
  delete receivedContentCollection[documentId]
  return {
    ...state,
    receivedContentCollection,
  }
}

function stateSetBoardsOrder(state, order) {
  return {
    ...state,
    app: {
      ...state.app,
      content: {
        order,
      },
    },
  }
}

function stateSetUserContentSettings(state, settings) {
  let { selectedContentId, selectedNodeId } = state.app
  if (selectedContentId === undefined) {
    selectedContentId = Object.keys(state.ownContentCollection).find(() => true)
    selectedNodeId = 0
  }

  return {
    ...state,
    app: {
      ...state.app,
      content: settings,
      selectedContentId,
      selectedNodeId,
    },
  }
}

export const initialState = {
  ownContentCollection: {},
  receivedContentCollection: {},
  contentCollection: {},
  contentUICollection: {},
  app: {
    permissions: [],
    content: {
      order: [],
    },
    undoStack: {
      undo: [],
      redo: [],
    },
    uploading: {},
    loading: false,
  },
}

export default (state = {}, action) => {
  switch (action.type) {
    case RESET_STATE:
      return initialState

    case CREATE_CONTENT:
      return stateCreateContent(state, action.contentId, action.userId)
    case UPDATE_CONTENT_COLLECTION:
      return stateUpdateContentCollection(
        state,
        action.modified,
        action.removed,
      )
    case DELETE_CONTENT:
      return stateDeleteContent(state, action.contentId)

    case SET_NOT_EDITING:
      return stateSetNotEditing(state)
    case SET_ICON:
      return stateSetIcon(state, action.nodeId, action.icon)
    case SET_TITLE:
      return stateSetTitle(state, action.nodeId, action.title, action.editing)
    case SET_TEXT:
      return stateSetText(state, action.nodeId, action.text, action.editing)
    case SET_UPLOADING_IMAGE:
      return stateSetUploadingImage(state, action.nodeId, action.image)
    case REMOVE_UPLOADING:
      return stateRemoveUploading(state, action.image)
    case ADD_FOLDER:
      return stateAddFolder(state, action.parentId)
    case ADD_AND_SELECT_FOLDER:
      return stateAddAndSelectFolder(state, action.parentId)
    case ADD_TEXT_NODE:
      return stateAddTextNode(state, action.parentId)
    case ADD_AND_SELECT_TEXT_NODE:
      return stateAddAndSelectTextNode(state, action.parentId)
    case ADD_UPLOADING_IMAGE_NODE:
      return stateAddUploadingImageNode(state, action.parentId, action.image)
    case MOVE_NODE:
      return stateMoveNode(state, action.nodeId, action.newIndex)
    case REMOVE_NODE:
      return stateRemoveNode(state, action.nodeId)

    case SELECT_NODE_ID:
      return stateSelectNodeId(state, action.nodeId, action.contentId, true)
    case SELECT_PARENT_FOLDER:
      return stateSelectParentFolder(state)
    case END_EDITING_CONTENT_NODE:
      return stateEndEditingContentNode(state)
    case TOGGLE_EXPANDED:
      return stateToggleExpanded(state, action.folderId, action.contentId)

    case UNDO_CONTENT_CHANGE:
      return stateUndoRedoContentChange(state, true)
    case REDO_CONTENT_CHANGE:
      return stateUndoRedoContentChange(state, false)

    case UPDATE_PERMISSIONS:
      return stateUpdatePermissions(state, action.emails)

    case ADD_RECEIVED_DOCUMENT:
      return addReceivedDocument(state, action.document, action.documentOwnerId)
    case REMOVE_RECEIVED_DOCUMENT:
      return removeReceivedDocument(state, action.documentId)

    case SET_BOARDS_ORDER:
      return stateSetBoardsOrder(state, action.order)
    case SET_USER_CONTENT_SETTINGS:
      return stateSetUserContentSettings(state, action.settings)

    default:
      return state
  }
}
